import type { DeclarationAnswer, DeclarationStatus } from '../../domain/declaration/schema';

/**
 * The signed-in employee's own declaration — owned by this feature, implemented
 * in `infrastructure/` (§3.4, §4.1).
 */

export interface MyDeclaration {
  readonly id: string;
  readonly policyYear: string;
  readonly status: DeclarationStatus;
  /** Non-sensitive answers, from the main document. */
  readonly answers: readonly DeclarationAnswer[];
  /** Sensitive answers, from the separate medical subcollection. */
  readonly medicalAnswers: readonly DeclarationAnswer[];
  readonly submittedAt: string | null;
  readonly reviewNoteAr: string | null;
}

export type DeclarationFailureCode =
  | 'forbidden'
  | 'submissions_closed'
  | 'not_editable'
  | 'network'
  | 'unknown';

export class DeclarationError extends Error {
  readonly code: DeclarationFailureCode;

  constructor(code: DeclarationFailureCode, message: string) {
    super(message);
    this.name = 'DeclarationError';
    this.code = code;
  }
}

export interface DeclarationPort {
  /** The caller's declaration for a policy year, or null if not started. */
  getMyDeclaration(policyYear: string): Promise<MyDeclaration | null>;

  /**
   * Saves a draft. Writes the two documents — main and medical — so the split
   * that keeps health data out of listings is maintained on every save, not
   * only on submit.
   */
  saveDraft(
    policyYear: string,
    answers: readonly DeclarationAnswer[],
    medicalAnswers: readonly DeclarationAnswer[],
  ): Promise<void>;

  /**
   * Submits.
   *
   * The only status change an employee may make, and the rules permit it only
   * from `draft` or `needs_documents`. Everything afterwards belongs to the
   * committee.
   */
  submit(): Promise<void>;
}
